import React, { useState } from 'react'
import { useEventHistoryQuery } from '../NewCompanyPage/EventHistory/useEventHistoryQuery'
import { EventHistoryList } from '../NewCompanyPage/EventHistory/EventHistoryList'
import { EmailsModal } from '../NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { buildEmailFromEvent } from '../NewCompanyPage/EventHistory/utils'
import { useEventHistoryFilterStore } from '../NewCompanyPage/EventHistory/EventHistoryFilterStore'
import { useMountEffect } from '../../hooks/useMountEffect'
import { Skeleton } from '../FbUI/Skeleton'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import type { EmailMessage } from '../../models/emailIntegration'

interface RecommendationActivityTabProps {
  action: EnrichedAction
}

/**
 * Skeleton mirroring the current ``EventDateGroup`` layout — a date header
 * (label + hairline) above a gray day container of timeline rows (round event
 * icon + stacked text lines). Built on the DS2026 ``Skeleton`` primitive so it
 * stays on-token, and shown in place of ``EventHistoryList``'s spinner overlay
 * so the panel doesn't flash a centered loader before the timeline arrives.
 */
const ActivityTimelineSkeleton: React.FC = () => (
  <div className="mt-4 space-y-6">
    {[0, 1].map((groupIdx) => (
      <div key={groupIdx} className="space-y-2">
        {/* Date header — label + trailing hairline */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-28" />
          <div className="h-px flex-1 bg-line" />
        </div>
        {/* Gray day container with timeline rows */}
        <div className="space-y-4 rounded-lg bg-neutral-50 p-3">
          {[0, 1].map((rowIdx) => (
            <div key={rowIdx} className="flex gap-3">
              <Skeleton className="size-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5 pt-1">
                <Skeleton className="h-3.5 w-1/2" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

/**
 * Activity timeline for the recommendation right-panel. Reuses the same
 * ``EventHistoryList`` + ``useEventHistoryQuery`` stack the deal/company
 * pages render. Rendered only when the user selects the Activity tab so
 * the request fires lazily.
 *
 * Always company-scoped (``chainProxyId`` = ``contact_company_id``). The
 * deal-scoped activity endpoint only joins emails whose
 * ``EmailThread.deal_id`` matches — most outreach is threaded to the
 * company, not a specific deal, so deal-scoping silently drops the bulk
 * of email events. Recs care about the full company picture (matching
 * what the synthesis pipeline saw at gen time) so we mirror that here.
 *
 * The shared filter store is reset on mount because it's a global
 * zustand store — without this, filters from a previously visited deal
 * or company page would leak into the panel feed.
 */
export const RecommendationActivityTab: React.FC<
  RecommendationActivityTabProps
> = ({ action }) => {
  useMountEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  })

  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    // Coerce null → undefined so the hook can skip the fetch cleanly
    // for contact_suggestion ``create`` recs (no company assigned yet).
    chainProxyId: action.contact_company_id ?? undefined,
    selectedTab: 'all',
  })

  // First-load skeleton: ``EventHistoryList``'s built-in loading state
  // is a centered spinner overlay, which feels jumpy in the sider.
  // Replace it with placeholder rows on the initial fetch (no data yet).
  // Once events have arrived, defer back to the list so background
  // refetches don't blow away the existing timeline.
  const showInitialSkeleton = isLoading && groupedEvents.length === 0

  return (
    <>
      {showInitialSkeleton ? (
        <ActivityTimelineSkeleton />
      ) : (
        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={() => {}}
          onEmailClick={(event) => setSelectedEmail(buildEmailFromEvent(event))}
        />
      )}
      {selectedEmail && (
        <EmailsModal
          show={!!selectedEmail}
          onHide={() => setSelectedEmail(null)}
          emailOnlyMode
          initialEmail={selectedEmail}
        />
      )}
    </>
  )
}
